import React, { useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import SpectrumBlock3D from './SpectrumBlock3D';

const BOARD_ROWS = 20;
const BOARD_COLUMNS = 10;

/**
 * SpectrumInstancedBoard — renders placed blocks as individual SpectrumBlock3D
 * components so they get the same premium gem effects (spinning core, sheen sweep,
 * corner sparkles, rainbow bands, neon edges) as the falling piece.
 *
 * Starburst rays are excluded via isPlaced=true.
 */
const SpectrumInstancedBoard = ({
  boardMatrix,
  boardVersion = 0,
}) => {
  // Force re-render on demand-mode canvases (opponent boards)
  const { invalidate } = useThree();

  // Parse board matrix into positioned blocks
  const blocks = useMemo(() => {
    if (!boardMatrix) return [];

    const result = [];
    for (let row = 0; row < boardMatrix.length; row++) {
      const rowData = boardMatrix[row];
      if (!rowData) continue;

      const colLimit = Math.min(rowData.length, BOARD_COLUMNS);
      for (let col = 0; col < colLimit; col++) {
        const cell = rowData[col];
        if (cell && cell !== 0 && cell !== '' && typeof cell === 'string') {
          const x = col - BOARD_COLUMNS / 2 + 0.5;
          const y = BOARD_ROWS - row - 0.5;
          result.push({
            key: `${row}-${col}`,
            position: [x, y, 0],
            colorClass: cell,
          });
        }
      }
    }

    // Trigger re-render for demand-mode canvases
    invalidate();

    return result;
  }, [boardMatrix, boardVersion, invalidate]);

  return (
    <group>
      {blocks.map((block) => (
        <SpectrumBlock3D
          key={block.key}
          position={block.position}
          colorClass={block.colorClass}
          isGhost={false}
          isActive={false}
          isPlaced={true}
          animate={true}
          scale={1}
        />
      ))}
    </group>
  );
};

export default SpectrumInstancedBoard;
